import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Typography, TextField, Paper, Container, Grid2 as Grid, Link } from '@mui/material';
import { PageHeader } from 'components';
import { InfoMessage } from 'components/InfoMessage';
import { useGa4, useDebounce } from 'hooks';
import { additionalErrorTexts, getAnalyticsAccountChecklist, inputAccordionData } from 'shared';
import { numberInputStyling } from 'shared/ui';
import { GA4CheckList, GA4CheckListItem } from 'components/GA4Parts';

const gridWidth = { xs: 1, md: '824px' };

export const Ga4StandAlone = () => {
  const {
    getGa4InfoQuery,
    ga4PropertyId,
    setGa4PropertyId,
    updateGa4PropertyIdMutation,
    checkGa4ConnectionMutation,
    isConnectionSuccess,
  } = useGa4();
  const [isInitialPropSettled, setIsInitialPropSettled] = useState(false);
  const [isPropertyIdInvalid, setIsPropertyIdInvalid] = useState(false);

  const ga4Info = getGa4InfoQuery.data;

  const debouncedUpdateGa4PropertyId = useDebounce(updateGa4PropertyIdMutation.mutate, 500);

  const changeHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.value.length) {
        setIsPropertyIdInvalid(true);
      } else {
        setIsPropertyIdInvalid(false);
      }
      setGa4PropertyId(e.target.value);
      debouncedUpdateGa4PropertyId();
    },
    [debouncedUpdateGa4PropertyId, setGa4PropertyId]
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  const setInputError = () => {
    setIsPropertyIdInvalid(true);
    inputRef.current && inputRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!ga4PropertyId && ga4Info?.ga4PropertyId && !isInitialPropSettled) {
      setGa4PropertyId(ga4Info?.ga4PropertyId);
      setIsInitialPropSettled(true);
    }
  }, [ga4Info, ga4PropertyId, isInitialPropSettled, setGa4PropertyId, isConnectionSuccess]);

  const GA4Config = useMemo(() => {
    return getAnalyticsAccountChecklist({
      checkGa4ConnectionMutation: () => checkGa4ConnectionMutation.mutate({ sendSuccessEmail: true }),
      shouldShowAdditionalError: isConnectionSuccess === false,
      PropertyIdInput: null,
      setInputError,
      ga4PropertyId,
    });
  }, [checkGa4ConnectionMutation, ga4PropertyId, isConnectionSuccess]);

  return (
    <Stack sx={{ bgcolor: 'primary.light', height: '100vh' }}>
      <PageHeader />
      <Stack sx={{ height: '100%', overflow: 'auto', flexGrow: 1 }}>
        <Container sx={{ mt: 10, mb: 10, flexGrow: 1, maxWidth: '1250px !important', p: '0px !important' }}>
          <Paper sx={{ p: 5, pb: 6, display: 'flex', justifyContent: 'center' }} component="form">
            <Grid container direction="column" spacing={4} justifyContent="center" maxWidth={gridWidth}>
              <Stack gap={6}>
                <Typography variant="h1" align="center" mb={2}>
                  Google Analytics 4 mit Alloy verbinden
                </Typography>
                <Stack p={5}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={500}
                    lineHeight="28px"
                    align="center"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }} component={'b'}>
                      {ga4Info &&
                        `${ga4Info.createdBy.firstName} ${ga4Info.createdBy.lastName} (${ga4Info.createdBy.email}) benötigt deine Unterstützung.`}
                    </Typography>
                    <br />
                    Für einige Alloy-Kampagnen muss dein GA4-Konto verknüpfen werden, damit Alloy das User Engagement
                    auswerten kann.
                  </Typography>
                </Stack>
                <InfoMessage
                  fullWidth
                  title="Du brauchst Zugang zu GA4 und Adminrechte um folgenden Schritte auszuführen"
                />
                {GA4Config[0] && <GA4CheckList checkList={[GA4Config[0]]} />}
                <GA4CheckListItem
                  checkListItem={{
                    title: '2. Schritt - Gib hier die GA4 Property-ID ein',
                    additionalError: isConnectionSuccess === false ? additionalErrorTexts : null,
                    description: 'Gib die Property-ID ein zu der Ringier Advertising  Zugang erhalten soll.',
                    Content: () => null,
                    accordion: {
                      Content: () => inputAccordionData,
                    },
                  }}
                >
                  <TextField
                    ref={inputRef}
                    autoComplete="off"
                    label="Property ID"
                    value={ga4PropertyId}
                    error={isPropertyIdInvalid}
                    helperText={isPropertyIdInvalid ? 'Gib bitte die Property-ID ein' : ''}
                    type="number"
                    sx={{
                      width: { xs: 1, md: 1 / 3 },
                      mb: 2,
                      ...numberInputStyling,
                      '& .MuiInputBase-input.MuiInputBase-input': { background: '#ffffff' },
                    }}
                    onChange={changeHandler}
                  />
                </GA4CheckListItem>
                {GA4Config[2] && GA4Config[3] && <GA4CheckList checkList={[GA4Config[2], GA4Config[3]]} />}

                {isConnectionSuccess && (
                  <InfoMessage
                    fullWidth
                    variant="success"
                    title="Super, die Verbindung hat geklappt!"
                    message={
                      <Typography>
                        Wir haben eine{' '}
                        <Typography sx={{ fontWeight: 'bold' }} component={'b'}>
                          Bestätigungsmail an {ga4Info?.createdBy.email} verschickt
                        </Typography>
                        , dass die Verbindung nun steht. Danke für deine Unterstützung!
                      </Typography>
                    }
                  />
                )}
                {isConnectionSuccess === false && (
                  <InfoMessage
                    fullWidth
                    variant="error"
                    title="Verbindung fehlgeschlagen"
                    message={
                      <Typography>
                        Ooops, etwas ist schiefgelaufen. Überprüfe die Schritte oben und versuch es nochmals. Benötigst
                        du Unterstützung?{' '}
                        <Link href={'#'} color={'text.primary'} target="_blank">
                          Hier findest du weitere Hilfe.
                        </Link>
                      </Typography>
                    }
                  />
                )}
              </Stack>
            </Grid>
          </Paper>
        </Container>
      </Stack>
    </Stack>
  );
};
